import useMergedState from '../hooks/useMergedState';

export interface FieldSchema {
  value?: string;
  required?: boolean;
  errorMessage?: string;
}

export type GenericForm<T extends string> = Record<T, FieldSchema>;

interface UseFormValidatorOptions<T extends string> {
  initialForm: GenericForm<T>;
  either?: T[][];
}

const useBucketSpecificationValidators = <T extends string>({
  initialForm,
  either = [],
}: UseFormValidatorOptions<T>) => {
  const [formValues, setFormValues] =
    useMergedState<GenericForm<T>>(initialForm);

  const [errors, setErrors] = useMergedState<Record<T, string | undefined>>(
    Object.keys(initialForm).reduce(
      (acc, key) => {
        acc[key as T] = undefined;
        return acc;
      },
      {} as Record<T, string | undefined>,
    ),
  );

  const onChange = (field: T, value: string | undefined) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        value,
      },
    }));

    setErrors((prev) => {
      const updatedErrors = { ...prev };

      // Clear error for the field being changed
      updatedErrors[field] = undefined;

      // Also check if it's part of an "either" group
      for (const group of either) {
        if (group.includes(field)) {
          const isAnyFilled = group.some(
            (key) =>
              (key === field && value?.trim()) ||
              (key !== field && formValues[key]?.value?.trim() !== ''),
          );

          // If any in the group is filled, clear all errors in the group
          if (isAnyFilled) {
            group.forEach((key) => {
              updatedErrors[key] = undefined;
            });
          }
        }
      }

      return updatedErrors;
    });
  };

  const validate = () => {
    let hasError = false;
    const tempErrors: Record<T, string | undefined> = { ...errors };

    // Regular "required" fields
    (Object.keys(formValues) as T[]).forEach((key) => {
      const field = formValues[key];
      if (field.required && (!field.value || field.value.trim() === '')) {
        tempErrors[key] = field.errorMessage || `${key} is required`;
        hasError = true;
      }
    });

    // Either/or validation
    for (const group of either) {
      const isAnyFilled = group.some(
        (field) =>
          formValues[field]?.value && formValues[field]?.value?.trim() !== '',
      );

      if (!isAnyFilled) {
        group.forEach((field) => {
          tempErrors[field] =
            formValues[field]?.errorMessage ||
            `At least one of [${group.join(', ')}] is required`;
        });
        hasError = true;
      }
    }

    setErrors(tempErrors);
    return !hasError;
  };

  return {
    formValues,
    setFormValues,
    onChange,
    errors,
    validate,
  };
};

export default useBucketSpecificationValidators;
